import { FormEvent, KeyboardEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { ArrowDown, SendHorizontal, Smile } from "lucide-react"
import { linkifyText } from "utils/linkifyText"
import { CHAT_MESSAGE_MAX_LENGTH } from "./callSignals"
import { CallEmojiPicker } from "./CallEmojiPicker"
import type { ChatMessage } from "./useCallChat"

const CHAT_LINK_CLASS = "break-all text-[#f6a35c] underline underline-offset-2 hover:text-[#ffd4b0]"

const timeFormatter = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" })

type ChatPanelProps = {
  messages: ChatMessage[]
  onSend: (text: string) => boolean
}

/**
 * Room chat matching Prebuilt's Chat sidebar: newest at the bottom with names and times, a
 * 256-character composer with a counter, an emoji picker, Enter to send (Shift+Enter for a
 * new line), and a Scroll to bottom control when new messages arrive while scrolled up.
 */
export function ChatPanel({ messages, onSend }: ChatPanelProps) {
  const [draft, setDraft] = useState("")
  const [pickerOpen, setPickerOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const [pickerHeight, setPickerHeight] = useState(360)
  useLayoutEffect(() => {
    if (!pickerOpen) return
    let frame = 0
    const measure = () => {
      const height = panelRef.current?.getBoundingClientRect().height
      if (height && Number.isFinite(height)) setPickerHeight(Math.max(160, Math.min(360, Math.floor(height / 2))))
    }
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure) }
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(schedule)
    if (panelRef.current) observer?.observe(panelRef.current)
    window.addEventListener("resize", schedule)
    window.visualViewport?.addEventListener("resize", schedule)
    measure()
    return () => {
      observer?.disconnect(); cancelAnimationFrame(frame)
      window.removeEventListener("resize", schedule)
      window.visualViewport?.removeEventListener("resize", schedule)
    }
  }, [pickerOpen])
  const [composerNotice, setComposerNotice] = useState("")
  const [pinnedToBottom, setPinnedToBottom] = useState(true)
  const [hasUnseen, setHasUnseen] = useState(false)
  const listRef = useRef<HTMLDivElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const pickerRef = useRef<HTMLDivElement | null>(null)
  const pickerButtonRef = useRef<HTMLButtonElement | null>(null)
  const lastCountRef = useRef(messages.length)

  useEffect(() => {
    if (!pickerOpen) return
    const handleKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return
      setPickerOpen(false)
      pickerButtonRef.current?.focus()
    }
    const handlePointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null
      if (!target || pickerRef.current?.contains(target) || pickerButtonRef.current?.contains(target)) return
      setPickerOpen(false)
    }
    document.addEventListener("keydown", handleKey)
    document.addEventListener("mousedown", handlePointer)
    document.addEventListener("touchstart", handlePointer)
    return () => {
      document.removeEventListener("keydown", handleKey)
      document.removeEventListener("mousedown", handlePointer)
      document.removeEventListener("touchstart", handlePointer)
    }
  }, [pickerOpen])

  const scrollToBottom = useCallback(() => {
    const list = listRef.current
    if (!list) return
    list.scrollTop = list.scrollHeight
    setHasUnseen(false)
    setPinnedToBottom(true)
  }, [])

  useEffect(() => {
    const grew = messages.length > lastCountRef.current
    lastCountRef.current = messages.length
    if (!grew) return
    if (pinnedToBottom || messages[messages.length - 1]?.local) {
      scrollToBottom()
    } else {
      setHasUnseen(true)
    }
  }, [messages, pinnedToBottom, scrollToBottom])

  const handleScroll = () => {
    const list = listRef.current
    if (!list) return
    const atBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 24
    setPinnedToBottom(atBottom)
    if (atBottom) setHasUnseen(false)
  }

  const submit = () => {
    if (onSend(draft)) {
      setDraft("")
      setComposerNotice("")
      setPickerOpen(false)
      textareaRef.current?.focus()
    }
  }
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    submit()
  }
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      submit()
    }
  }
  const insertEmoji = (emoji: string) => {
    // Keep complex emoji whole when the composer is near its character limit.
    const start = textareaRef.current?.selectionStart ?? draft.length
    const end = textareaRef.current?.selectionEnd ?? draft.length
    const next = draft.slice(0, start) + emoji + draft.slice(end)
    if (next.length > CHAT_MESSAGE_MAX_LENGTH) {
      setComposerNotice("Not enough space for this emoji. Shorten your message first.")
    } else {
      setDraft(next)
      setComposerNotice("")
    }
    setPickerOpen(false)
    textareaRef.current?.focus()
  }

  return (
    <div ref={panelRef} data-testid="call-chat-panel" className="call-chat-content flex h-full min-h-0 flex-col">
      <div ref={listRef} onScroll={handleScroll} data-testid="call-chat-messages" className="relative min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <p className="text-xs text-white/50">No messages yet. Chat is only visible to people in the call right now.</p>
        ) : (
          messages.map((message) => (
            <div key={message.id} data-testid="call-chat-message" className="text-sm text-white">
              <div className="flex items-baseline gap-2">
                <span className="truncate font-semibold">{message.local ? "You" : message.name}</span>
                <time className="shrink-0 text-[11px] text-white/50" dateTime={new Date(message.sentAt).toISOString()}>{timeFormatter.format(message.sentAt)}</time>
              </div>
              <p className="whitespace-pre-wrap break-words leading-relaxed text-white/90">{linkifyText(message.text, { linkClassName: CHAT_LINK_CLASS })}</p>
            </div>
          ))
        )}
      </div>
      {hasUnseen && (
        <div className="relative">
          <button
            type="button"
            onClick={scrollToBottom}
            aria-label="Scroll to bottom"
            className="absolute -top-10 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-[#f6a35c] px-3 py-1 text-xs font-semibold text-[#1c2530] shadow-lg"
          >
            <ArrowDown className="h-3.5 w-3.5" />
            New messages
          </button>
        </div>
      )}
      <form onSubmit={handleSubmit} className="relative shrink-0 border-t border-white/10 p-3">
        {pickerOpen && (
          <div ref={pickerRef} role="dialog" aria-label="Chat emoji" data-testid="call-chat-emoji-picker" className="call-chat-picker relative z-50 mb-2 rounded-xl bg-[#0f1419] shadow-2xl ring-1 ring-white/10">
            <CallEmojiPicker height={pickerHeight} onSelect={insertEmoji} onClose={() => { setPickerOpen(false); pickerButtonRef.current?.focus() }} />
          </div>
        )}
        <div className="flex items-end gap-2 rounded-xl border border-white/15 bg-[#1c2530] px-2 py-1.5 focus-within:border-[#f6a35c]">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(event) => { setDraft(event.target.value.slice(0, CHAT_MESSAGE_MAX_LENGTH)); setComposerNotice("") }}
            onKeyDown={handleKeyDown}
            placeholder="Type a message…"
            aria-label="Type a message"
            maxLength={CHAT_MESSAGE_MAX_LENGTH}
            rows={1}
            data-testid="call-chat-input"
            className="min-w-0 max-h-32 min-h-[2rem] flex-1 resize-none bg-transparent py-1 text-sm text-white placeholder:text-white/40 focus:outline-none"
          />
          <button ref={pickerButtonRef} type="button" aria-label="Open emoji picker" aria-haspopup="dialog" aria-expanded={pickerOpen} onClick={() => setPickerOpen((value) => !value)} className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white">
            <Smile className="h-4 w-4" />
          </button>
          <button type="submit" aria-label="Submit" disabled={draft.trim().length === 0} data-testid="call-chat-submit" className="rounded-md p-1.5 text-[#f6a35c] hover:bg-white/10 disabled:opacity-40">
            <SendHorizontal className="h-4 w-4" />
          </button>
        </div>
        {composerNotice && <p role="status" className="mt-1 text-xs text-[#ffd4b0]">{composerNotice}</p>}
        <p data-testid="call-chat-limit" className="mt-1 text-[11px] text-white/40">
          {draft.length > 0 ? `${draft.length}/${CHAT_MESSAGE_MAX_LENGTH} char limit` : `${CHAT_MESSAGE_MAX_LENGTH} char limit`}
        </p>
      </form>
    </div>
  )
}
